import { combinedSearchApi, ICombinedSearchCandidate } from 'api/CombinedSearchAPI'
import { CvId } from 'types/ids'
import { ISearchResult } from 'types/searchInterfaces'
import { IPerson } from 'types/userInterfaces'

/**
 * Maps a combined-search candidate (backend field names) onto the shared `ISearchResult` shape
 * the rest of the Search page renders (matching the old free-text search's field names, e.g.
 * `semantic_zscore`/`keyword_score` rather than `semanticZscore`/`keywordScore`), so `ResultCard`/
 * `FreeTextMatchInfo`/`matchQuality.ts` work identically regardless of which search produced the
 * result. `Person` is a placeholder - combined-search candidates aren't hydrated yet at this point
 * (see Results/index.tsx's lazy per-page hydration via `search/results`).
 */
export function mapCombinedCandidateToSearchResult(candidate: ICombinedSearchCandidate): ISearchResult {
  return {
    kind: candidate.kind,
    anonymous: candidate.anonymous,
    personId: candidate.personId,
    cvId: candidate.cvId,
    Person: {} as IPerson,
    score: candidate.score,
    matchedSkills: candidate.matchedSkills,
    similarSkills: candidate.similarSkills,
    matchedCertificates: candidate.matchedCertificates,
    similarCertificates: candidate.similarCertificates,
    matchedCompetences: candidate.matchedCompetences,
    similarCompetences: candidate.similarCompetences,
    chunkType: candidate.chunkType,
    contentText: candidate.contentText,
    semantic_score: candidate.semanticScore,
    semantic_zscore: candidate.semanticZscore,
    keyword_score: candidate.keywordScore,
    llmRelevant: candidate.llmRelevant,
    llmConfidence: candidate.llmConfidence,
    llmReason: candidate.llmReason,
  }
}

/**
 * Hydrates a batch of lightweight combined-search results (from `mapCombinedCandidateToSearchResult`,
 * carrying a `cvId` and no `Person` yet) into full Person data via `combinedSearchApi.postResults`,
 * preserving each result's score/kind/match data. Callers decide the batch size - the main Search
 * page hydrates one page at a time (see `Results/index.tsx`), while a capped-result flow like
 * `ProposeToRole.tsx` can hydrate its whole (already-capped) result set in one call.
 *
 * @param results - The lightweight results to hydrate.
 * @param controller - Optional AbortController.
 * @returns Hydrated results, in the same relative order as `results`, minus any candidate the
 *   backend couldn't resolve (deleted person/CV, stale id, etc. - see `hydrateResults` in
 *   caleo-back). Empty input short-circuits without a request.
 */
export async function hydrateSearchResults(
  results: ISearchResult[],
  controller?: AbortController
): Promise<ISearchResult[]> {
  if (results.length === 0) {
    return results
  }

  const hydrated = await combinedSearchApi.postResults(
    results.map(r => ({ cvId: r.cvId as CvId, personId: r.personId, anonymous: r.anonymous })),
    controller
  )

  // The response can be shorter than the request (unresolved candidates are dropped, not
  // nulled - see caleo-back's hydrateResults), so results can't be paired up by index. Named
  // candidates are matched back by personId/cvId; anonymous ones get a fresh, unrelated
  // anonymousId/anonymousCvId each call, so they're matched in order among themselves instead,
  // which `Promise.all` + filter preserves on the backend.
  const namedByKey = new Map(
    hydrated.filter(result => !result.anonymous).map(result => [`${result.personId}:${result.cvId}`, result])
  )
  const anonymousQueue = hydrated.filter(result => result.anonymous)

  const matched: ISearchResult[] = []
  for (const candidate of results) {
    const hydratedResult = candidate.anonymous
      ? anonymousQueue.shift()
      : namedByKey.get(`${candidate.personId}:${candidate.cvId}`)

    if (!hydratedResult) {
      continue
    }

    matched.push({
      ...candidate,
      anonymousId: hydratedResult.anonymousId,
      anonymousCvId: hydratedResult.anonymousCvId,
      Person: hydratedResult.Person,
    })
  }

  return matched
}
